import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  createProduct,
  deleteProduct,
  getProductById,
  getProducts,
  getRelatedProducts,
  updateProduct,
} from "../../../services/product";

function useFetchProduct(filters = {}, customCallbacks = {}) {
  const queryClient = useQueryClient();
  // Get all products query
  const {
    data: productsData,
    isLoading: isGettingProducts,
    error: errGettingProducts,
  } = useQuery({
    queryKey: ["products", filters],
    queryFn: () => getProducts(filters),
  });

  // Get single product query
  const {
    data: product,
    isLoading: isGettingProduct,
    error: errGettingProduct,
  } = useQuery({
    queryKey: ["product"],
    queryFn: (productId) => getProductById(productId),
    enabled: false, // This query will not run automatically
  });

  // Create product mutation
  const { mutate: createProductFn, isLoading: isCreatingProduct } = useMutation(
    {
      mutationFn: (productData) => {
        return createProduct(productData);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["products"]);
        toast.success("Product created successfully");
        if (customCallbacks.onCreateSuccess) {
          customCallbacks.onCreateSuccess();
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Something went wrong");
      },
    }
  );

  // Update product mutation
  const { mutate: updateProductFn, isLoading: isUpdatingProduct } = useMutation(
    {
      mutationFn: ({ productId, productData }) => {
        return updateProduct(productId, productData);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["products"]);
        toast.success("Product updated successfully");
        if (customCallbacks.onUpdateSuccess) {
          customCallbacks.onUpdateSuccess();
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || "Something went wrong");
      },
    }
  );

  // Delete product mutation
  const { mutate: deleteProductFn, isLoading: isDeletingProduct } = useMutation(
    {
      mutationFn: (productId) => deleteProduct(productId),
      onSuccess: () => {
        queryClient.invalidateQueries(["products"]);
        toast.success("Product deleted successfully");
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || "Something went wrong");
      },
    }
  );

  // Function to fetch a single product
  const getOneProductFn = async (productId) => {
    try {
      if (productId === undefined) return;
      const response = await getProductById(productId);
      queryClient.setQueryData(["product"], response);
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };

  // GET RELATED PRODUCTS
  const getRelatedProductsFn = async (productId) => {
    try {
      const response = await getRelatedProducts(productId);
      queryClient.setQueryData(["related-products", response]);
      return response;
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    }
  };
  return {
    // CREATE
    createProductFn,
    isCreatingProduct,

    // READ ALL
    isGettingProducts,
    errGettingProducts:
      errGettingProducts?.response?.data?.message ||
      errGettingProducts?.message,
    products: productsData || [],
    totalPages: productsData?.pagination?.totalPages || 1,
    currentPage: productsData?.pagination?.currentPage || 1,

    // READ ONE
    getOneProductFn,
    isGettingProduct,
    errGettingProduct:
      errGettingProduct?.response?.data?.message || errGettingProduct?.message,
    product,

    // UPDATE PRODUCT
    updateProductFn,
    isUpdatingProduct,

    // DELETE PRODUCT
    deleteProductFn,
    isDeletingProduct,

    // GET RELATED PRODUCTS
    getRelatedProductsFn,
    isGettingRelated: getRelatedProductsFn.isLoading,
    errorGettingRelated: getRelatedProductsFn.error,
  };
}

export default useFetchProduct;
